import React, { useCallback, useEffect, useState } from 'react';

import { isSome } from 'fp-ts/lib/Option';
import { DeleteDashboardCriteriaBuilder } from 'use-case/dashboard/delete/deleteDashboardCriteria';
import { useDashboard } from 'adapter/in/dashboards/context/Dashboard';
import ConfirmChangesModal from 'adapter/in/common/components/ConfirmActionModal/ConfirmActionModal';
import i18n from 'asset/translation';
import gtmIds from 'adapter/in/dashboards/config/gtm';

const gtm = gtmIds.dashboard.deleteModal;

export const useOnDeleteDashboardModal = (onSuccess: () => void) => {
    const { delete: remove, deleteError, deleteIsLoading, deleteResult, deleteReset } = useDashboard();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [dashboardForDeletion, setDashboardForDeletion] = useState('');

    const handleOnDelete = useCallback(
        (dashboardId: string) => {
            setShowDeleteModal(true);
            setDashboardForDeletion(dashboardId);
        },
        [deleteResult]
    );

    const handleOnDeleteConfirm = useCallback(
        () => !!dashboardForDeletion && remove(DeleteDashboardCriteriaBuilder.create().withId(dashboardForDeletion).build()),
        [dashboardForDeletion]
    );

    useEffect(() => {
        if (isSome(deleteResult)) {
            setShowDeleteModal(false);
            setDashboardForDeletion('');
            deleteReset();
            onSuccess();
        }
    }, [deleteResult]);

    const onDeleteModal = (
        <ConfirmChangesModal
            title={i18n.t('dashboards.actionDelete')}
            isLoading={deleteIsLoading}
            hasError={isSome(deleteError)}
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onProceed={handleOnDeleteConfirm}
            message={i18n.t('dashboards.delete.warningMsg')}
            proceedButtonText={i18n.t('dashboards.delete.yes')}
            proceedButtonVariant='destructive'
            dataIdContext={{
                proceed: gtm.actions.delete,
                cancel: gtm.actions.cancel,
                tryAgain: gtm.actions.tryAgain,
            }}
        />
    );

    return { handleOnDelete, onDeleteModal };
};
